import type { DrawingStateCreator, InputSlice } from './types';

export const createInputSlice: DrawingStateCreator<InputSlice> = (set, get) => ({
  modifiers: {
    shiftPressed: false,
    altPressed: false,
    ctrlPressed: false,
    spacePressed: false,
  },

  touch: {
    isTouchDevice:
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || navigator.maxTouchPoints > 0),
    isPinching: false,
    initialPinchDistance: null,
    lastTouchPoints: null,
  },

  setModifier: (modifier, pressed) =>
    set((state) => {
      state.modifiers[modifier] = pressed;
      // Note: We don't change the active tool when space is pressed
      // The tool UI should remain the same, only the cursor and behavior change
    }),

  handleKeyDown: (e) => {
    const state = get();

    // Sync modifier state from the event's modifier flags on every keystroke.
    // A bare `if (e.key === 'Shift')` toggle gets stuck-on whenever the keyup is
    // swallowed by a focus loss (DevTools shortcut, alt-tab, browser shortcut
    // popups) — and then the pencil draws straight lines until the user manually
    // presses+releases Shift again. Re-reading the modifier flags here keeps the
    // store honest as soon as the user types anything.
    state.setModifier('shiftPressed', e.shiftKey);
    state.setModifier('altPressed', e.altKey);
    state.setModifier('ctrlPressed', e.ctrlKey || e.metaKey);
    if (e.key === ' ') {
      e.preventDefault();
      state.setModifier('spacePressed', true);
    }

    // Tool shortcuts - use 'code' for layout-independent shortcuts
    if (!e.ctrlKey && !e.metaKey) {
      switch (e.code) {
        case 'KeyP':
          state.setTool('pencil');
          break;
        case 'KeyE':
          state.setTool('eraser');
          break;
        case 'KeyC':
          if (e.altKey) {
            // Clone source is now set via Alt+Click
            // This keyboard shortcut is disabled
          } else {
            state.setTool('clone');
          }
          break;
        case 'KeyT':
          state.setTool('text');
          break;
        case 'KeyX':
          state.toggleTool();
          break;
        case 'BracketLeft':
          state.setBrushSize(state.brushSettings.size - 5);
          break;
        case 'BracketRight':
          state.setBrushSize(state.brushSettings.size + 5);
          break;
      }
    }

    // History shortcuts
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        state.undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        state.redo();
      }
    }
  },

  handleKeyUp: (e) => {
    const state = get();

    // Mirror keydown: re-read all modifier flags from the event rather than
    // toggling per-key. Same rationale (missed keyups during focus loss).
    state.setModifier('shiftPressed', e.shiftKey);
    state.setModifier('altPressed', e.altKey);
    state.setModifier('ctrlPressed', e.ctrlKey || e.metaKey);
    if (e.key === ' ') {
      e.preventDefault();
      state.setModifier('spacePressed', false);
    }
  },

  handleTouchStart: (touches) =>
    set((state) => {
      if (touches.length === 2 && touches[0] && touches[1]) {
        const distance = Math.hypot(
          touches[1].clientX - touches[0].clientX,
          touches[1].clientY - touches[0].clientY
        );
        state.touch.initialPinchDistance = distance;
        state.touch.isPinching = true;
      } else if (touches.length === 1) {
        state.touch.isPinching = false;
        state.touch.initialPinchDistance = null;
      }

      state.touch.lastTouchPoints = Array.from(touches);
    }),

  handleTouchMove: (touches) => {
    const { touch, canvas } = get();

    if (
      touches.length === 2 &&
      touches[0] &&
      touches[1] &&
      touch.isPinching &&
      touch.initialPinchDistance
    ) {
      const distance = Math.hypot(
        touches[1].clientX - touches[0].clientX,
        touches[1].clientY - touches[0].clientY
      );

      const scale = distance / touch.initialPinchDistance;

      set((state) => {
        state.canvas.transform.zoom = Math.max(
          0.1,
          Math.min(5, canvas.transform.zoom * scale)
        );
        state.touch.initialPinchDistance = distance;
      });
    }

    set((state) => {
      state.touch.lastTouchPoints = Array.from(touches);
    });
  },

  handleTouchEnd: () =>
    set((state) => {
      state.touch.isPinching = false;
      state.touch.initialPinchDistance = null;
      state.touch.lastTouchPoints = null;
    }),

  handlePinch: (distance) => {
    const { touch } = get();

    if (!touch.initialPinchDistance) {
      set((state) => {
        state.touch.initialPinchDistance = distance;
      });
      return;
    }

    const scale = distance / touch.initialPinchDistance;

    set((state) => {
      state.canvas.transform.zoom *= scale;
      state.touch.initialPinchDistance = distance;
    });
  },
});
